import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/http/client";
import type { SessionUser, Role } from "@/types/session";

export const userKeys = {
  all: ["users"] as const,
  list: () => [...userKeys.all, "list"] as const,
};

export function useUsers() {
  return useQuery({
    queryKey: userKeys.list(),
    queryFn: () => api.get<SessionUser[]>("/users"),
  });
}

export type UserCreate = {
  name: string;
  password: string;
  email: string;
  role?: Role;
  status?: "Activo" | "Inactivo";
};

export type UserUpdate = {
  name?: string;
  password?: string;
  email?: string;
  role?: Role;
  status?: "Activo" | "Inactivo";
};

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserCreate) => api.post<SessionUser>("/users", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UserUpdate }) =>
      api.put<SessionUser>(`/users/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useToggleUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ ok: true; status: "Activo" | "Inactivo" }>(`/users/${id}/toggle-status`),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ ok: true; tempPassword: string }>(`/users/${id}/reset-password`),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}
